/* Hallmark · component: host flash · genre: editorial · theme: host_ui.css (designed-as-app)
 * states: default · hover · focus · active · disabled · loading · error · success
 * contrast: pass (40–41) · responsive: pass (320 / 375 / 414 / 768 px, light + dark)
 * Hallmark · pre-emit critique: P5 H5 E5 S5 R5 V4
 */
import React, { useEffect, useState } from "react";
import {
  CircleCheckIcon,
  InfoIcon,
  LoaderCircleIcon,
  TriangleAlertIcon,
  XIcon,
} from "lucide-react";

import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "@javascript/components/ui/alert";
import { Button } from "@javascript/components/ui/button";
import { cn } from "@javascript/components/utils";

export type HostFlashState = "error" | "success" | "notice";
export type HostFlashForcedState = "hover" | "focus" | "active" | "disabled" | "loading";

const stateVisuals = {
  error: {
    label: "Needs attention",
    Icon: TriangleAlertIcon,
    accentClassName: "text-destructive",
  },
  success: {
    label: "Success",
    Icon: CircleCheckIcon,
    accentClassName: "text-success",
  },
  notice: {
    label: "Notice",
    Icon: InfoIcon,
    accentClassName: "text-primary",
  },
} as const;

export function HostFlash({
  state,
  message,
  onDismiss,
  forcedState,
  className,
}: {
  state: HostFlashState;
  message: string;
  onDismiss: () => void;
  forcedState?: HostFlashForcedState;
  className?: string;
}) {
  const visual = stateVisuals[state];
  const loading = forcedState === "loading";
  const disabled = forcedState === "disabled" || loading;
  const [hovered, setHovered] = useState(false);
  const [focusWithin, setFocusWithin] = useState(false);

  useEffect(() => {
    if (hovered || focusWithin) return;

    const timeout = window.setTimeout(onDismiss, 5_000);

    return () => window.clearTimeout(timeout);
  }, [focusWithin, hovered, onDismiss]);

  return (
    <Alert
      role={state === "error" ? "alert" : "status"}
      aria-live={state === "error" ? "assertive" : "polite"}
      aria-atomic="true"
      data-flash-state={state}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocusCapture={() => setFocusWithin(true)}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocusWithin(false);
      }}
      className={cn(
        "pointer-events-none fixed inset-x-3 bottom-3 z-50 w-auto max-w-md gap-y-1 rounded-lg border-border bg-popover px-4 py-3 text-popover-foreground shadow-lg has-[>svg]:gap-x-3 sm:inset-x-auto sm:bottom-4 sm:left-4 sm:w-full",
        className,
      )}
    >
      <visual.Icon
        aria-hidden="true"
        data-slot="host-flash-icon"
        data-tone={state}
        className={cn("size-5", visual.accentClassName)}
      />
      <AlertTitle className={cn("font-serif text-sm font-semibold leading-5", visual.accentClassName)}>
        {visual.label}
      </AlertTitle>
      <AlertDescription className="break-words text-sm leading-5 text-popover-foreground">
        {message}
      </AlertDescription>
      <AlertAction>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled}
          aria-label={loading ? "Dismissing notification" : "Dismiss notification"}
          onClick={onDismiss}
          className={cn(
            "pointer-events-auto size-11 rounded-lg text-muted-foreground",
            forcedState === "hover" && "bg-accent text-accent-foreground",
            forcedState === "focus" && "border-ring ring-[3px] ring-ring/50",
            forcedState === "active" && "translate-y-px opacity-90",
          )}
        >
          {loading ? (
            <LoaderCircleIcon aria-hidden="true" className="animate-spin motion-reduce:animate-none" />
          ) : (
            <XIcon aria-hidden="true" />
          )}
        </Button>
      </AlertAction>
    </Alert>
  );
}
